/**
 * Lucy In-Conversation Checkout — WowStore AI
 * Powered by Shopify Storefront API
 *
 * Flow:
 *   1. Lucy recommends product → "Buy Now" button appears on product card
 *   2. Click "Buy Now" → Storefront API creates cart → get checkoutUrl
 *   3. Mini checkout panel slides up INSIDE Lucy chat (no redirect)
 *   4. "Checkout with Shop Pay" → instant payment overlay
 *   5. Conversation continues uninterrupted
 *
 * API: Shopify Storefront API 2024-01
 * Token: Public (client-safe)
 */

(function() {
  'use strict';

  if (window._lucyStorefrontInstance) return;
  window._lucyStorefrontInstance = Date.now();

  /* ═══════════════════════ Config ═══════════════════════ */
  var STOREFRONT_ENDPOINT = 'https://' + window.Shopify.shop + '/api/2024-01/graphql.json';
  var STOREFRONT_TOKEN = '202a7ebd05964342afc4a2f997817bb9';
  var PRODUCT_JSON_SUFFIX = '.js';

  /* ═══════════════════════ State ═══════════════════════ */
  var activeCart = null;       // { id, checkoutUrl, items[], total }
  var variantCache = {};       // productUrl → variantData
  var panel = null;            // DOM reference to checkout panel
  var isOpen = false;

  /* ═══════════════════════ Storefront API ═══════════════════════ */

  function storefrontQuery(query, variables) {
    return fetch(STOREFRONT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function(r) { return r.json(); });
  }

  var CREATE_CART_MUTATION = `
    mutation cartCreate($lines: [CartLineInput!]!, $buyerIdentity: CartBuyerIdentityInput) {
      cartCreate(input: { lines: $lines, buyerIdentity: $buyerIdentity }) {
        cart {
          id
          checkoutUrl
          lines(first: 10) {
            nodes {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price { amount currencyCode }
                  product {
                    title
                    featuredImage { url(transform: { maxWidth: 120, maxHeight: 120 }) }
                    handle
                  }
                }
              }
              cost {
                totalAmount { amount currencyCode }
              }
            }
          }
          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }
        }
        userErrors { field message }
      }
    }
  `;

  var ADD_LINES_MUTATION = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          lines(first: 10) {
            nodes {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price { amount currencyCode }
                  product {
                    title
                    featuredImage { url(transform: { maxWidth: 120, maxHeight: 120 }) }
                    handle
                  }
                }
              }
              cost { totalAmount { amount currencyCode } }
            }
          }
          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }
        }
        userErrors { field message }
      }
    }
  `;

  /* ═══════════════════════ Product Helpers ═══════════════════════ */

  function getVariantId(productUrl) {
    return new Promise(function(resolve, reject) {
      var handle = extractHandle(productUrl);
      if (!handle) return reject(new Error('No product handle'));
      if (variantCache[handle]) return resolve(variantCache[handle]);

      fetch('/products/' + handle + PRODUCT_JSON_SUFFIX)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var product = data.product || data;
          if (!product || !product.variants || !product.variants.length) {
            return reject(new Error('No variants'));
          }
          // Find first available variant
          var variant = product.variants.find(function(v) { return v.available; }) || product.variants[0];
          var price = typeof variant.price === 'number' && variant.price > 100
            ? (variant.price / 100).toFixed(2)
            : String(variant.price);

          var result = {
            // Storefront API needs GID format
            storefrontId: 'gid://shopify/ProductVariant/' + variant.id,
            variantId: variant.id,
            variantTitle: variant.title,
            available: variant.available !== false,
            productTitle: product.title,
            price: price,
            image: (product.images && product.images[0]) ? product.images[0].src : null,
            handle: handle
          };

          variantCache[handle] = result;
          resolve(result);
        })
        .catch(reject);
    });
  }

  function extractHandle(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function formatMoney(amount, currency) {
    var num = parseFloat(amount);
    if (isNaN(num)) return amount;
    var symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
    return symbol + num.toFixed(2);
  }

  /* ═══════════════════════ Cart Operations ═══════════════════════ */

  function createCart(storefrontVariantId, quantity) {
    quantity = quantity || 1;
    return storefrontQuery(CREATE_CART_MUTATION, {
      lines: [{ merchandiseId: storefrontVariantId, quantity: quantity }]
    }).then(function(response) {
      if (response.errors) throw new Error(response.errors[0].message);
      var result = response.data.cartCreate;
      if (result.userErrors && result.userErrors.length) {
        throw new Error(result.userErrors[0].message);
      }
      activeCart = result.cart;
      return activeCart;
    });
  }

  function addToExistingCart(storefrontVariantId, quantity) {
    if (!activeCart) return createCart(storefrontVariantId, quantity);
    return storefrontQuery(ADD_LINES_MUTATION, {
      cartId: activeCart.id,
      lines: [{ merchandiseId: storefrontVariantId, quantity: quantity || 1 }]
    }).then(function(response) {
      if (response.errors) throw new Error(response.errors[0].message);
      var result = response.data.cartLinesAdd;
      if (result.userErrors && result.userErrors.length) {
        throw new Error(result.userErrors[0].message);
      }
      activeCart = result.cart;
      return activeCart;
    });
  }

  /* ═══════════════════════ Checkout Panel UI ═══════════════════════ */

  function buildPanel() {
    if (panel) return panel;

    panel = document.createElement('div');
    panel.id = 'lucy-checkout-panel';
    panel.className = 'lucy-checkout-panel';
    panel.innerHTML = `
      <div class="lcp-header">
        <button class="lcp-back" id="lcpBack">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Back to Lucy
        </button>
        <span class="lcp-secure">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          Secure
        </span>
      </div>

      <div class="lcp-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
        Your Order
      </div>

      <div class="lcp-items" id="lcpItems">
        <!-- Items populated dynamically -->
      </div>

      <div class="lcp-total" id="lcpTotal">
        <span>Total</span>
        <span id="lcpTotalAmount">—</span>
      </div>

      <div class="lcp-actions">
        <button class="lcp-shop-pay-btn" id="lcpShopPay">
          <svg width="22" height="14" viewBox="0 0 110 36" fill="white">
            <path d="M10.7 0C8 0 5.6 1.6 4.5 4L0 36h10.2l1.4-9h7.8l1.4 9H31L26.5 4C25.4 1.6 23 0 20.3 0h-9.6zm.8 19l2-13h4l2 13h-8zM35 0v36h9V22.5h5.5c6.7 0 10.5-3.8 10.5-11.3C60 4 56.3 0 49.5 0H35zm9 15V7h4.5c2.3 0 3.5 1 3.5 4s-1.2 4-3.5 4H44zM64 36V0h9v36h-9zM78 0v36h9V21h1l6 15h10L96 18.5C99.3 16.8 101 13.8 101 10c0-6.5-4.2-10-11-10H78zm9 14V7h4c2.3 0 3.5.9 3.5 3.5S93.3 14 91 14h-4z"/>
          </svg>
          Checkout with Shop Pay
        </button>

        <a class="lcp-checkout-link" id="lcpCheckoutLink" href="#" target="_blank">
          Other payment methods →
        </a>

        <button class="lcp-continue-btn" id="lcpContinue">
          ← Continue Shopping
        </button>
      </div>

      <div class="lcp-footer">
        🔒 Powered by WowStore AI · 5% of each sale to causes you choose
      </div>
    `;

    // Wire buttons
    panel.querySelector('#lcpBack').addEventListener('click', hidePanel);
    panel.querySelector('#lcpContinue').addEventListener('click', hidePanel);
    panel.querySelector('#lcpShopPay').addEventListener('click', function() {
      if (activeCart && activeCart.checkoutUrl) {
        var shopPayUrl = activeCart.checkoutUrl;
        // Prefer Shop Pay flow
        if (!shopPayUrl.includes('payment=')) {
          shopPayUrl += (shopPayUrl.includes('?') ? '&' : '?') + 'payment=shop_pay';
        }
        openCheckoutOverlay(shopPayUrl);
      }
    });

    return panel;
  }

  function showPanel(cartData) {
    var chatWindow = document.getElementById('lucyChatWindow');
    if (!chatWindow) return;

    var p = buildPanel();
    if (!chatWindow.contains(p)) {
      chatWindow.appendChild(p);
    }

    // Populate items
    var itemsEl = p.querySelector('#lcpItems');
    var totalEl = p.querySelector('#lcpTotalAmount');
    var checkoutLink = p.querySelector('#lcpCheckoutLink');

    itemsEl.innerHTML = '';

    if (cartData.lines && cartData.lines.nodes) {
      cartData.lines.nodes.forEach(function(line) {
        var merch = line.merchandise;
        var img = merch.product.featuredImage ? merch.product.featuredImage.url : '';
        var itemHtml = `
          <div class="lcp-item">
            <div class="lcp-item-img">
              ${img ? '<img src="' + img + '" alt="' + merch.product.title + '">' : '<div class="lcp-item-placeholder">🛍</div>'}
            </div>
            <div class="lcp-item-info">
              <div class="lcp-item-name">${merch.product.title}</div>
              <div class="lcp-item-variant">${merch.title !== 'Default Title' ? merch.title : ''}</div>
              <div class="lcp-item-price">${formatMoney(line.cost.totalAmount.amount, line.cost.totalAmount.currencyCode)}</div>
            </div>
            <div class="lcp-item-qty">×${line.quantity}</div>
          </div>
        `;
        itemsEl.insertAdjacentHTML('beforeend', itemHtml);
      });
    }

    if (cartData.cost) {
      totalEl.textContent = formatMoney(
        cartData.cost.totalAmount.amount,
        cartData.cost.totalAmount.currencyCode
      );
    }

    if (checkoutLink && cartData.checkoutUrl) {
      checkoutLink.href = cartData.checkoutUrl;
    }

    // Update Shop Pay button href
    var shopPayBtn = p.querySelector('#lcpShopPay');
    if (shopPayBtn && cartData.checkoutUrl) {
      shopPayBtn.dataset.checkoutUrl = cartData.checkoutUrl;
    }

    // Animate in
    requestAnimationFrame(function() {
      p.classList.add('lcp-open');
    });
    isOpen = true;
  }

  function hidePanel() {
    if (panel) {
      panel.classList.remove('lcp-open');
      isOpen = false;
    }
  }

  /* ═══════════════════════ Checkout Overlay ═══════════════════════ */

  function openCheckoutOverlay(url) {
    // Create fullscreen overlay for checkout
    var overlay = document.createElement('div');
    overlay.id = 'lucy-checkout-overlay';
    overlay.innerHTML = `
      <div class="lco-header">
        <button class="lco-close" id="lcoClose">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <span class="lco-title">Checkout</span>
        <span class="lco-powered">WowStore AI</span>
      </div>
      <div class="lco-body">
        <div class="lco-loading">
          <div class="lco-spinner"></div>
          <p>Opening secure checkout…</p>
        </div>
      </div>
      <a href="${url}" target="_blank" class="lco-open-new">Open in new tab →</a>
    `;

    document.body.appendChild(overlay);
    overlay.querySelector('#lcoClose').addEventListener('click', function() {
      overlay.remove();
    });

    // Attempt iframe (may be blocked by Shopify CSP - show fallback)
    requestAnimationFrame(function() {
      overlay.classList.add('lco-open');
      var body = overlay.querySelector('.lco-body');
      var iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.allow = 'payment';
      iframe.style.cssText = 'width:100%;height:100%;border:0;display:none;';
      iframe.onload = function() {
        overlay.querySelector('.lco-loading').style.display = 'none';
        iframe.style.display = 'block';
      };
      iframe.onerror = function() {
        // CSP blocked — show redirect fallback
        body.innerHTML = `
          <div class="lco-redirect">
            <div style="font-size:3rem">🛍️</div>
            <h3>Complete Your Order</h3>
            <p>Your cart is ready — tap below to complete checkout in Shop Pay.</p>
            <a href="${url}" class="lco-go-btn" target="_blank">Open Secure Checkout</a>
            <button class="lco-back-btn" onclick="document.getElementById('lucy-checkout-overlay').remove()">← Back to Chat</button>
          </div>
        `;
      };
      body.appendChild(iframe);
    });
  }

  /* ═══════════════════════ Main API ═══════════════════════ */

  function buyNow(productUrl, productData, buttonEl) {
    // Animate button
    if (buttonEl) {
      buttonEl.textContent = '✨ Loading…';
      buttonEl.disabled = true;
    }

    getVariantId(productUrl).then(function(variant) {
      var cartFn = activeCart ? addToExistingCart : createCart;
      return cartFn(variant.storefrontId, 1);
    }).then(function(cart) {
      if (buttonEl) {
        buttonEl.textContent = '✓ Added!';
        buttonEl.classList.add('lcp-added');
      }
      // Show mini checkout panel — call via window.LucyCheckout.showPanel so external overrides work
      window.LucyCheckout.showPanel(cart);

      // Also add to Shopify AJAX cart for header count
      getVariantId(productUrl).then(function(v) {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: v.variantId, quantity: 1 }] })
        }).then(function() {
          // Update cart count in header
          fetch('/cart.json').then(function(r) { return r.json(); }).then(function(c) {
            var countEls = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
            countEls.forEach(function(el) { el.textContent = c.item_count; });
          });
        });
      });
    }).catch(function(err) {
      console.error('[LucyCheckout] Error:', err);
      if (buttonEl) {
        buttonEl.textContent = '⚡ Buy Now';
        buttonEl.disabled = false;
      }
      // Fallback: go to product page
      if (productUrl) window.location.href = productUrl;
    });
  }

  /* ═══════════════════════ Public API ═══════════════════════ */

  window.LucyCheckout = {
    buyNow: buyNow,
    showPanel: showPanel,
    hidePanel: hidePanel,
    getCart: function() { return activeCart; },
    openCheckout: function() {
      if (activeCart) openCheckoutOverlay(activeCart.checkoutUrl);
    }
  };

})();
